"use client";

import { useEffect, useMemo, useState } from "react";

import { RealityModelDialog } from "@/components/scirix/reality-model/reality-model-dialog";
import { useWorkspaceRouteNamespace } from "@/components/workspaces/workspace-route-context";
import { RealityModelMark } from "@/components/scirix/reality-model/reality-model-mark";
import { resolveWorkspaceRoute } from "@/config/workspace-routes";
import { useAnalysisRunStatusQuery, useTriggerNamespaceAnalysisMutation } from "@/hooks/useAnalysisRuns";
import { useActiveNamespaceId, useActiveNamespaceSlug } from "@/hooks/useNamespaces";
import { useOptionalSession } from "@/hooks/useSession";
import { deriveRealityModelState, formatAgo, realityModelChipLabel } from "@/lib/reality-model/status";
import { cn } from "@/lib/utils";

/**
 * The Reality Model's place in the top bar: always visible, so people see
 * that Scirix keeps a living map of their code and whether it is current.
 * Clicking it opens the pop-up with the state in plain words and the
 * update action.
 */
export function TopBarAnalysisRunIndicator() {
  const sessionContext = useOptionalSession();
  if (!sessionContext || sessionContext.session.status !== "authenticated") {
    return null;
  }
  return <TopBarAnalysisRunIndicatorContent />;
}

function TopBarAnalysisRunIndicatorContent() {
  const routeNamespace = useWorkspaceRouteNamespace();
  const ambientNamespaceId = useActiveNamespaceId();
  const ambientSlug = useActiveNamespaceSlug();
  const namespaceId = (routeNamespace ? routeNamespace.namespaceId : ambientNamespaceId) ?? undefined;
  const slug = routeNamespace?.slug ?? ambientSlug;
  const { data, isLoading } = useAnalysisRunStatusQuery(namespaceId);
  const trigger = useTriggerNamespaceAnalysisMutation(namespaceId);
  const [open, setOpen] = useState(false);
  const [pulse, setPulse] = useState(0);
  const now = useNow(30_000);

  const state = useMemo(() => deriveRealityModelState(data), [data]);

  if (!namespaceId || (isLoading && !data)) {
    return null;
  }

  const label = realityModelChipLabel(state);
  const ago = state.phase === "fresh" ? formatAgo(state.finishedAt, now) : null;
  const progress =
    state.phase === "updating" && state.step
      ? ((state.step.index - 1 + (state.step.fraction ?? 0.5)) / state.step.total) * 100
      : null;
  // The step goes next to the state word: the one running, or the one an update failed on.
  const stepWord =
    state.step && state.phase === "updating"
      ? state.step.label
      : state.step && state.phase === "failed"
        ? `at ${state.step.label}`
        : null;
  const settingsRoute = resolveWorkspaceRoute("settings", slug);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        onMouseEnter={() => setPulse((n) => n + 1)}
        onFocus={() => setPulse((n) => n + 1)}
        aria-label={`Reality Model: ${label}${stepWord ? `, ${stepWord}` : ""}${ago ? `, updated ${ago}` : ""}`}
        title={ago ? `Updated ${ago}` : label}
        data-testid="reality-model-chip"
        className={cn(
          "group relative inline-flex min-w-0 items-center gap-1.5 overflow-hidden rounded-full py-0.5 pl-1 pr-2.5",
          "text-xs transition-colors",
          "bg-[color-mix(in_oklab,var(--token-accent-info-core)_10%,transparent)]",
          "hover:bg-[color-mix(in_oklab,var(--token-accent-info-core)_18%,transparent)]",
          state.phase === "updating" &&
            "bg-[color-mix(in_oklab,var(--token-accent-magenta-core)_12%,transparent)] hover:bg-[color-mix(in_oklab,var(--token-accent-magenta-core)_20%,transparent)]",
          state.phase === "failed" &&
            "bg-[color:var(--token-accent-danger-surface)] hover:bg-[color:var(--token-accent-danger-surface-strong)]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        )}
      >
        {/* The 16px artwork centred in a 20px slot stays pixel-sharp. */}
        <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center">
          <RealityModelMark phase={state.phase} step={state.step?.index} size={16} pulse={pulse} />
        </span>
        <span className="font-medium text-foreground">Reality Model</span>
        <span
          className={cn(
            "truncate text-muted-foreground",
            state.phase === "failed" && "text-[color:var(--token-accent-danger-foreground)]",
          )}
        >
          {label}
          {stepWord ? <span className="hidden sm:inline"> · {stepWord}</span> : null}
        </span>
        {progress !== null ? (
          <span aria-hidden className="absolute inset-x-0 bottom-0 h-px">
            <span
              className="block h-full bg-[color:var(--token-accent-magenta-core)] transition-[width] duration-500"
              style={{ width: `${Math.round(progress)}%` }}
            />
          </span>
        ) : null}
      </button>
      <RealityModelDialog
        open={open}
        onOpenChange={setOpen}
        state={state}
        now={now}
        settingsHref={settingsRoute ? `${settingsRoute}?section=analysis` : undefined}
        onUpdate={(mode) => trigger.mutateAsync({ incremental: mode === "incremental" })}
      />
    </>
  );
}

function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}
